"use client";

import { AdvancedMarker, Map, Pin } from "@vis.gl/react-google-maps";
import { useState } from "react";

import { type Prisma } from "@/.zenstack/logical-prisma-client";

import { isGoogleMapsReady } from "./GoogleMapsProvider";
import { ListingCard, type ListingCardProps } from "./listings/ListingCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Spinner } from "./ui/spinner";

type ListingWithAddress = Prisma.ListingGetPayload<{
  select: {
    id: true;
    title: true;
    details: true;
  };
}> &
  ListingCardProps["listing"];

interface MapViewProps {
  listings: ListingWithAddress[];
  googleMapId: string;
}

export default function MapView({ listings, googleMapId }: MapViewProps) {
  const [selectedListing, setSelectedListing] =
    useState<ListingWithAddress | null>(null);
  const [showListingDialog, setShowListingDialog] = useState(false);

  // Check if Google Maps API is fully ready using the utility function
  const isReady = isGoogleMapsReady();

  // Filter out listings without valid coordinates
  const validListings = listings.filter(
    (listing) =>
      listing.details.address &&
      listing.details.address.location &&
      listing.details.address.location.latitude != null &&
      listing.details.address.location.longitude != null &&
      !isNaN(listing.details.address.location.latitude) &&
      !isNaN(listing.details.address.location.longitude),
  );

  // Debug logging
  console.log("MapView render:", {
    googleMapId,
    totalListings: listings.length,
    validListings: validListings.length,
    isGoogleMapsReady,
    firstListing: validListings[0]
      ? {
          title: validListings[0].title,
          coordinates: {
            lat: validListings[0]!.details!.address!.location!.latitude,
            lng: validListings[0]!.details!.address!.location!.longitude,
          },
        }
      : null,
  });

  if (!isReady) {
    return <Spinner />;
  }

  // Handle single listing vs multiple listings differently
  const position =
    validListings.length === 1
      ? {
          lat:
            (validListings[0]?.details?.address?.location?.latitude || 0) -
            0.005, // magic amount to move the map up a bit in the window
          lng: validListings[0]?.details?.address?.location?.longitude || 0,
        }
      : {
          lat:
            (Math.max(
              ...validListings.map(
                (l) => l.details!.address!.location!.latitude!,
              ),
            ) +
              Math.min(
                ...validListings.map(
                  (l) => l.details!.address!.location!.latitude!,
                ),
              )) /
            2,
          lng:
            (Math.max(
              ...validListings.map(
                (l) => l.details!.address!.location!.longitude!,
              ),
            ) +
              Math.min(
                ...validListings.map(
                  (l) => l.details!.address!.location!.longitude!,
                ),
              )) /
            2,
        };

  // Set zoom level - very close for single listing, dynamic for multiple
  const defaultZoom =
    validListings.length === 1
      ? 16 // Very close zoom for single listing
      : validListings.length > 0
        ? Math.min(
            4.5, // Allow zooming out to national level
            Math.max(
              14, // Allow zooming out to continental level
              Math.floor(
                14 -
                  Math.log2(
                    Math.max(
                      Math.abs(
                        Math.max(
                          ...validListings.map(
                            (l) => l.details!.address!.location!.latitude!,
                          ),
                        ) -
                          Math.min(
                            ...validListings.map(
                              (l) => l.details!.address!.location!.latitude!,
                            ),
                          ),
                      ),
                      Math.abs(
                        Math.max(
                          ...validListings.map(
                            (l) => l.details!.address!.location!.longitude!,
                          ),
                        ) -
                          Math.min(
                            ...validListings.map(
                              (l) => l.details!.address!.location!.longitude!,
                            ),
                          ),
                      ),
                    ) * 1.5, // Increase the spread factor to zoom out more
                  ),
              ),
            ),
          )
        : 4.5; // Default to national level when no listings

  return (
    <>
      <div role="region" aria-label="Map view of listings">
        {validListings && validListings.length > 0 ? (
          <div className="h-[600px]" aria-label="Interactive map">
            {/* BUG: The map only renders the marker. Needs more debugging. */}
            <Map
              defaultCenter={position}
              defaultZoom={defaultZoom}
              mapId={googleMapId}
              mapTypeId="roadmap"
              gestureHandling="greedy"
              disableDefaultUI={false}
              tilt={0}
              heading={0}
              zoomControl={true}
            >
              {validListings.map((listing) => (
                <AdvancedMarker
                  key={listing.id}
                  position={{
                    lat: listing.details!.address!.location!.latitude!,
                    lng: listing.details!.address!.location!.longitude!,
                  }}
                  title={listing.title}
                  onClick={() => {
                    setSelectedListing(listing);
                    setShowListingDialog(true);
                  }}
                >
                  <Pin />
                </AdvancedMarker>
              ))}
            </Map>
          </div>
        ) : listings.length > 0 ? (
          <div className="flex h-[600px] items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
            <div className="text-center" role="status">
              <p className="text-gray-500 dark:text-gray-400">
                No listings with valid addresses
              </p>
            </div>
          </div>
        ) : (
          <div className="flex h-[600px] items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
            <div className="text-center" role="status">
              <p className="text-gray-500 dark:text-gray-400">
                No listings found
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Listing Details Dialog */}
      <Dialog
        open={showListingDialog}
        onOpenChange={setShowListingDialog}
        aria-label="Listing details"
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedListing?.title}</DialogTitle>
          </DialogHeader>
          {selectedListing && (
            <article className="mt-4">
              <ListingCard listing={selectedListing} />
            </article>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
